import React, {

  useCallback,

  useEffect,

  useMemo,

  useRef,

  useState,

} from 'react';



import clinicalNoteService from './clinicalNoteService.js';
import './ClinicalNoteRomCompact.css';



/**

 * Delay between the clinician's last edit and the autosave request.

 */

const AUTOSAVE_DELAY_MS = 900;



/**

 * Convert a backend numeric value into a controlled-input string.

 */

function toInputValue(value) {

  if (

    value === null ||

    value === undefined

  ) {

    return '';

  }



  return String(value);

}



/**

 * Convert an input value into Integer/null for Spring Boot.

 *

 * Blank values remain null while the note is in DRAFT.

 */

function toNullableInteger(value) {

  if (

    value === null ||

    value === undefined ||

    String(value).trim() === ''

  ) {

    return null;

  }



  const number =

    Number(value);



  if (!Number.isInteger(number)) {

    return null;

  }



  return number;

}



/**

 * Resolve ClinicalNoteMain.entityVersion from the normal note response.

 */

function resolveEntityVersion(response) {

  if (!response) {

    return null;

  }



  return (

    response.entityVersion ??

    response.note?.entityVersion ??

    response.clinicalNote?.entityVersion ??

    null

  );

}



/**

 * Stable frontend key for one ROM template/side combination.

 *

 * clinicalNoteRomId cannot be used because newly configured ROM fields

 * intentionally have a null database ID until their first save.

 */

function getRomKey(row) {

  return (

    `${row?.romTemplateId ?? 'unknown'}|` +

    `${row?.measurementSide ?? 'NONE'}`

  );

}



/**

 * Human-readable laterality.

 */

function formatSide(side) {

  switch (side) {

    case 'LEFT':

      return 'Left';



    case 'RIGHT':

      return 'Right';



    case 'BILATERAL':

      return 'Bilateral';



    case 'NOT_APPLICABLE':

      return '';



    default:

      return side || '';

  }

}



/**

 * Format a signed change value.

 */

function formatChange(value, unit) {

  if (

    value === null ||

    value === undefined

  ) {

    return '—';

  }



  const numeric =

    Number(value);



  if (!Number.isFinite(numeric)) {

    return '—';

  }



  const prefix =

    numeric > 0

      ? '+'

      : '';



  return `${prefix}${numeric}${unit ? ` ${unit}` : ''}`;

}



/**

 * Normalize one backend RomResponse for editing.

 */

function normalizeRomRow(row) {

  const draftStartingValue =
    (row?.measurementValue === null || row?.measurementValue === undefined)
      ? row?.defaultMeasurementValue
      : row?.measurementValue;

  return {

    ...row,

    draftMeasurementValue:
      toInputValue(
        draftStartingValue
      ),



    draftComments:

      row?.comments ?? '',

  };

}



/**

 * Sort ROM fields using backend display order, then display name,

 * then side.

 */

function sortRomRows(rows) {

  return [...rows].sort(

    (first, second) => {

      const firstOrder =

        Number.isFinite(

          Number(

            first?.displayOrder

          )

        )

          ? Number(

              first.displayOrder

            )

          : 0;



      const secondOrder =

        Number.isFinite(

          Number(

            second?.displayOrder

          )

        )

          ? Number(

              second.displayOrder

            )

          : 0;



      if (

        firstOrder !==

        secondOrder

      ) {

        return (

          firstOrder -

          secondOrder

        );

      }



      const nameComparison =

        String(

          first?.displayName ??

            ''

        ).localeCompare(

          String(

            second?.displayName ??

              ''

          )

        );



      if (

        nameComparison !== 0

      ) {

        return nameComparison;

      }



      return String(

        first?.measurementSide ??

          ''

      ).localeCompare(

        String(

          second?.measurementSide ??

            ''

        )

      );

    }

  );

}



/**

 * Validate one ROM draft before attempting autosave.

 *

 * Required fields are intentionally allowed to remain blank during

 * DRAFT editing. Complete-note validation will block review if a

 * required measurement remains missing.

 */

function validateRomRow(row) {

  const errors = {};



  const rawValue =

    row?.draftMeasurementValue ??

    '';



  if (

    rawValue !== '' &&

    !Number.isInteger(

      Number(rawValue)

    )

  ) {

    errors.measurementValue =

      'ROM measurement must be a whole number.';



    return errors;

  }



  const measurementValue =

    toNullableInteger(

      rawValue

    );



  if (

    measurementValue === null

  ) {

    return errors;

  }



  if (

    row?.minimumValue !== null &&

    row?.minimumValue !==

      undefined &&

    measurementValue <

      Number(

        row.minimumValue

      )

  ) {

    errors.measurementValue =

      `${row.displayName || 'ROM'} cannot be lower than ` +

      `${row.minimumValue}${row.unit ? ` ${row.unit}` : ''}.`;

  }



  if (

    row?.maximumValue !== null &&

    row?.maximumValue !==

      undefined &&

    measurementValue >

      Number(

        row.maximumValue

      )

  ) {

    errors.measurementValue =

      `${row.displayName || 'ROM'} cannot be higher than ` +

      `${row.maximumValue}${row.unit ? ` ${row.unit}` : ''}.`;

  }



  return errors;

}



/**

 * Calculate the live change from the previous treatment date.

 */

function calculateLiveChange(row) {

  const current =

    toNullableInteger(

      row?.draftMeasurementValue

    );



  const previous =

    row?.previousMeasurementValue;



  if (

    current === null ||

    previous === null ||

    previous === undefined

  ) {

    return null;

  }



  const previousNumber =

    Number(previous);



  if (

    !Number.isFinite(

      previousNumber

    )

  ) {

    return null;

  }



  return (

    current -

    previousNumber

  );

}



/**

 * Determine whether the current unsaved value would exceed the

 * configured maximum daily increase.

 *

 * This is only a frontend heads-up.

 *

 * The backend still makes the authoritative warning decision and

 * never automatically changes the clinician's ROM measurement.

 */

function predictsIncreaseWarning(row) {

  const change =

    calculateLiveChange(

      row

    );



  const maximumIncrease =

    row?.maxDailyIncrease;



  if (

    change === null ||

    maximumIncrease === null ||

    maximumIncrease ===

      undefined

  ) {

    return false;

  }



  const limit =

    Number(

      maximumIncrease

    );



  if (

    !Number.isFinite(limit) ||

    limit < 0

  ) {

    return false;

  }



  return change > limit;

}



/**

 * Merge a successful RomResponse back into the current editor row.

 *

 * If the clinician changed the row while the save request was in

 * flight, preserve the newer draft values while refreshing server

 * metadata such as previous-value comparison and warning state.

 */

function mergeSavedRomRow(

  currentRow,

  savedResponse,

  preserveDraft

) {

  const savedNormalized =

    normalizeRomRow(

      savedResponse

    );



  if (!preserveDraft) {

    return savedNormalized;

  }



  return {

    ...savedNormalized,



    draftMeasurementValue:

      currentRow

        .draftMeasurementValue,



    draftComments:

      currentRow

        .draftComments,

  };

}



/**

 * One editable ROM measurement row.

 */

function RomMeasurementRow({

  row,

  disabled,

  status,

  saveError,

  validationError,

  onMeasurementChange,

  onCommentsChange,

  onRetry,

}) {
  // Presentation state only. The editor and its autosave refs stay mounted.
  const [showComparison, setShowComparison] = useState(false);
  const [showComments, setShowComments] = useState(false);

  const sideLabel =

    formatSide(

      row.measurementSide

    );



  const liveChange =

    calculateLiveChange(

      row

    );



  const predictedIncreaseWarning =

    predictsIncreaseWarning(

      row

    );



  const hasIncreaseWarning =

    Boolean(

      row.increaseWarning

    );



  const hasRepetitionWarning =

    Boolean(

      row.repetitionWarning

    );



  const hasWarning =

    hasIncreaseWarning ||

    hasRepetitionWarning;



  const unresolvedWarning =

    hasWarning &&

    !row.warningAcknowledged;



  const currentDraftValue =
    toNullableInteger(
      row?.draftMeasurementValue
    );

  const hasRequiredValue =
    currentDraftValue !== null &&
    !validationError?.measurementValue;

  const usingDefaultValue =
    hasRequiredValue &&
    row?.defaultMeasurementValue !== null &&
    row?.defaultMeasurementValue !== undefined &&
    Number(currentDraftValue) ===
      Number(row.defaultMeasurementValue);

  const requiredState =
    !row.required
      ? 'optional'
      : !hasRequiredValue
        ? 'red'
        : usingDefaultValue
          ? 'yellow'
          : 'green';



  const minimum =

    row.minimumValue;



  const maximum =

    row.maximumValue;



  const rangeText =

    minimum !== null &&

    minimum !== undefined &&

    maximum !== null &&

    maximum !== undefined

      ? `${minimum}–${maximum}`

      : minimum !== null &&

          minimum !== undefined

        ? `Minimum ${minimum}`

        : maximum !== null &&

            maximum !==

              undefined

          ? `Maximum ${maximum}`

          : null;



  let statusText =

    'Saved';



  switch (status) {

    case 'saving':

      statusText =

        'Saving…';

      break;



    case 'unsaved':

      statusText =

        'Unsaved';

      break;



    case 'invalid':

      statusText =

        'Needs correction';

      break;



    case 'error':

      statusText =

        'Autosave failed';

      break;



    default:

      statusText =

        disabled

          ? 'Read only'

          : 'Saved';

  }



  return (

    <div

      style={{

        border:

          unresolvedWarning? '1px solid #d6a100'

            : '1px solid #ddd',



        borderRadius: 9,



        background:

          unresolvedWarning

            ? '#fffdf5'

            : '#fff',



        padding: 10,

      }}
      className="clinical-rom-measurement-card"
    >

      {/* ===================================================

          ROW HEADER

          =================================================== */}

      <div

        style={{

          display: 'flex',

          justifyContent:

            'space-between',

          gap: 12,

          alignItems:

            'flex-start',

          flexWrap: 'wrap',

          marginBottom: 12,

        }}

      >

        <div>

          <div

            style={{

              display: 'flex',

              alignItems: 'center',

              gap: 7,

              flexWrap: 'wrap',

            }}

          >

            <strong

              style={{

                fontSize: 15,

              }}

            >

              {row.displayName ||

                row.romCode ||

                'ROM Measurement'}

            </strong>



            {sideLabel && (

              <span

                style={{

                  display:

                    'inline-block',

                  borderRadius: 999,

                  padding:

                    '2px 7px',

                  fontSize: 11,

                  fontWeight: 700,

                  background:

                    '#eef3f8',

                }}

              >

                {sideLabel}

              </span>

            )}



            {row.required && (

              <span

                title={
                  requiredState === 'green'
                    ? 'Required measurement is complete.'
                    : requiredState === 'yellow'
                      ? 'Using the configured default value. This is passable but should be reviewed.'
                      : 'Required measurement is still incomplete.'
                }

                style={{
                  display: 'inline-block',
                  borderRadius: 999,
                  padding: '2px 7px',
                  fontSize: 11,
                  fontWeight: 700,
                  background:
                    requiredState === 'green'
                      ? '#e8f5e9'
                      : requiredState === 'yellow'
                        ? '#fff3cd'
                        : '#fce8e6',
                  color:
                    requiredState === 'green'
                      ? '#1f6b2a'
                      : requiredState === 'yellow'
                        ? '#795500'
                        : '#8a1c13',
                }}

              >
                {requiredState === 'green'
                  ? 'Required ✓'
                  : requiredState === 'yellow'
                    ? 'Default ⚠'
                    : 'Required'}

              </span>

            )}

          </div>



          <div

            style={{

              marginTop: 4,

              fontSize: 12,

              color: '#666',

            }}

          >

            {row.romCode

              ? `${row.romCode}`

              : 'ROM'}



            {rangeText

              ? ` • Range ${rangeText}${row.unit ? ` ${row.unit}` : ''}`

              : ''}

          </div>

        </div>



        <div

          aria-live="polite"

          style={{

            fontSize: 12,

            fontWeight: 700,

            color:

              status === 'error'

                ? '#b42318'

                : status ===

                    'invalid'

                  ? '#9a6700'

                  : status ===

                      'saving'

                    ? '#555'

                    : '#2e6a35',

          }}

        >

          {statusText}

        </div>

      </div>



      <div className="clinical-rom-quick-actions">
        <button type="button" onClick={() => setShowComparison((current) => !current)}
          aria-expanded={showComparison}
          aria-label={`${showComparison ? 'Hide' : 'Show'} previous ROM comparison for ${row.displayName || row.romCode || 'measurement'}`}>
          {showComparison ? 'Hide comparison' : 'Previous / Change'}
        </button>
        <button type="button" onClick={() => setShowComments((current) => !current)}
          aria-expanded={showComments}>
          {showComments ? 'Hide comments' : `Comments${row.draftComments?.trim() ? ' • Added' : ''}`}
        </button>
        {liveChange !== null && (
          <span className={`clinical-rom-quick-change${predictedIncreaseWarning ? ' is-warning' : ''}`}>
            Change: {formatChange(liveChange, row.unit)}
          </span>
        )}
      </div>

      {/* ===================================================

          VALUE + PREVIOUS COMPARISON

          =================================================== */}

      <div
        className={`clinical-rom-measurement-grid${showComparison ? ' show-comparison' : ''}`}
        style={{

          display: 'grid',

          gridTemplateColumns: 'repeat(auto-fit, minmax(145px, 1fr))',

          gap: 12,

          alignItems: 'start',

        }}

      >

        <div>

          <label

            htmlFor={`rom-value-${getRomKey(row)}`}

            style={{

              display: 'block',

              fontSize: 13,

              fontWeight: 700,

              marginBottom: 5,

            }}

          >

            Measurement



            {row.required && (

              <span

                aria-hidden="true"

                style={{

                  marginLeft: 4,

                  color: '#b42318',

                }}

              >

                *

              </span>

            )}

          </label>



          <div

            style={{

              display: 'flex',

              alignItems: 'center',

              gap: 7,

            }}

          >

            <input

              id={`rom-value-${getRomKey(row)}`}

              type="number"

              step="1"

              value={

                row.draftMeasurementValue

              }

              min={

                row.minimumValue ??

                undefined

              }

              max={

                row.maximumValue ??

                undefined

              }

              disabled={disabled}

              onChange={(event) =>

                onMeasurementChange(

                  event.target.value

                )

              }

              aria-invalid={Boolean(

                validationError

              )}

              style={{

                width: '100%',

                boxSizing:

                  'border-box',

                minHeight: 40,

                border:

                  validationError

                    ? '1px solid #b42318'

                    : '1px solid #bbb',

                borderRadius: 6,

                padding:

                  '7px 9px',

                fontSize: 15,

                background:

                  disabled

                    ? '#f3f4f6'

                    : '#fff',

              }}

            />



            {row.unit && (

              <span

                style={{

                  fontSize: 13,

                  color: '#555',

                  minWidth: 32,

                }}

              >

                {row.unit}

              </span>

            )}

          </div>



          {validationError && (

            <div

              role="alert"

              style={{

                marginTop: 5,

                fontSize: 12,

                color: '#b42318',

              }}

            >

              {validationError}

            </div>

          )}

        </div>



        <div>

          <div

            style={{

              fontSize: 11,

              fontWeight: 700,

              textTransform:

                'uppercase',

              color: '#777',

              marginBottom: 5,

            }}

          >

            Previous

          </div>



          <div

            style={{

              fontSize: 14,

              minHeight: 40,

              display: 'flex',

              alignItems: 'center',

            }}

          >

            {row.previousMeasurementValue !==

              null &&

            row.previousMeasurementValue !==

              undefined

              ? `${row.previousMeasurementValue}${row.unit ? ` ${row.unit}` : ''}`

              : 'No previous value'}

          </div>

        </div>



        <div>

          <div

            style={{

              fontSize: 11,

              fontWeight: 700,

              textTransform:

                'uppercase',

              color: '#777',

              marginBottom: 5,

            }}

          >

            Change

          </div>



          <div

            style={{

              fontSize: 14,

              minHeight: 40,

              display: 'flex',

              alignItems: 'center',

              fontWeight:

                predictedIncreaseWarning

                  ? 700

                  : 400,

              color:

                predictedIncreaseWarning

                  ? '#9a6700'

                  : '#222',

            }}

          >

            {formatChange(

              liveChange,

              row.unit

            )}

          </div>

        </div>



        <div>

          <div

            style={{

              fontSize: 11,

              fontWeight: 700,

              textTransform:

                'uppercase',

              color: '#777',

              marginBottom: 5,

            }}

          >

            Max Daily Increase

          </div>



          <div

            style={{

              fontSize: 14,

              minHeight: 40,

              display: 'flex',

              alignItems: 'center',

            }}

          >

            {row.maxDailyIncrease !==

              null &&

            row.maxDailyIncrease !==

              undefined

              ? `+${row.maxDailyIncrease}${row.unit ? ` ${row.unit}` : ''}`

              : 'Not configured'}

          </div>

        </div>

      </div>



      {/* ===================================================

          LIVE INCREASE HEADS-UP

          =================================================== */}

      {predictedIncreaseWarning &&

        !hasIncreaseWarning && (

          <div

            style={{

              marginTop: 10,

              border:

                '1px solid #d6a100',

              background:

                '#fff8df',

              borderRadius: 7,

              padding: 10,

              fontSize: 13,

            }}

          >

            <strong>

              Possible ROM increase

              warning:

            </strong>{' '}

            this value increases more

            than the configured daily

            maximum compared with the

            previous treatment date.



            <div

              style={{

                marginTop: 4,

                color: '#666',

              }}

            >

              The value will not be

              changed automatically.

              The backend will make

              the final warning

              determination when it

              autosaves.

            </div>

          </div>

        )}



      {/* ===================================================

          COMMENTS

          =================================================== */}

      <div

        style={{

          marginTop: 12,

        }}

      >

        {showComments ? (
          <div>
            <label htmlFor={`rom-comments-${getRomKey(row)}`}
              className="clinical-rom-comments-label">Optional ROM comments</label>
        <textarea

          id={`rom-comments-${getRomKey(row)}`}

          value={

            row.draftComments

          }

          disabled={disabled}

          rows={2}

          onChange={(event) =>

            onCommentsChange(

              event.target.value

            )

          }

          placeholder="Optional ROM comments"

          style={{

            width: '100%',

            boxSizing:

              'border-box',

            resize: 'vertical',

            border:

              '1px solid #bbb',

            borderRadius: 6,

            padding: 9,

            fontSize: 13,

            lineHeight: 1.4,

            background:

              disabled

                ? '#f3f4f6'

                : '#fff',

          }}

        />
          </div>
        ) : (
          row.draftComments?.trim() ? (
            <div className="clinical-rom-comments-summary" title={row.draftComments}>
              {row.draftComments}
            </div>
          ) : null
        )}

      </div>



      {/* ===================================================

          SAVED BACKEND WARNINGS

          =================================================== */}

      {hasWarning && (

        <div

          role="alert"

          style={{

            marginTop: 12,

            border:

              '1px solid #d6a100',

            borderRadius: 8,

            background:

              '#fff8df',

            padding: 11,

          }}

        >

          <div

            style={{

              fontWeight: 700,

              marginBottom: 5,

            }}

          >

            ROM Warning

          </div>



          {hasIncreaseWarning && (

            <div

              style={{

                fontSize: 13,

                marginBottom:

                  hasRepetitionWarning

                    ? 5

                    : 0,

              }}

            >

              • ROM increased more

              than the configured

              maximum from the

              previous treatment

              date.

            </div>

          )}



          {hasRepetitionWarning && (

            <div

              style={{

                fontSize: 13,

              }}

            >

              • ROM remained

              unchanged for too many

              consecutive notes.

            </div>

          )}



          <div

            style={{

              marginTop: 7,

              fontSize: 12,

              color: '#555',

            }}

          >

            {row.warningAcknowledged

              ? 'This warning has been acknowledged.'

              : 'This warning remains unresolved and must be addressed or acknowledged before the note completes review.'}

          </div>



          {row.warningAcknowledged &&

            row.warningReason && (

              <div

                style={{

                  marginTop: 5,

                  fontSize: 12,

                  color: '#555',

                }}

              >

                Reason:{' '}

                {row.warningReason}

              </div>

            )}

        </div>

      )}



      {/* ===================================================

          AUTOSAVE ERROR

          =================================================== */}

      {saveError && (

        <div

          role="alert"

          style={{

            marginTop: 12,

            border:

              '1px solid #b42318',

            borderRadius: 8,

            background:

              '#fff5f5',

            padding: 11,

          }}

        >

          <strong>

            ROM measurement was not

            saved.

          </strong>



          <div

            style={{

              marginTop: 4,

              fontSize: 13,

            }}

          >

            {saveError}

          </div>



          {!disabled && (

            <button

              type="button"

              onClick={onRetry}

              style={{

                marginTop: 8,

              }}

            >

              Retry Save

            </button>

          )}

        </div>

      )}

    </div>

  );

}



/**

 * ROM editor for one clinical note.

 *

 * Features:

 *

 * - displays every configured ROM field returned by GET /rom

 * - supports unsaved placeholder fields

 * - supports LEFT / RIGHT bilateral records separately

 * - displays previous treatment value

 * - displays live change from previous

 * - gives an immediate maximum-increase heads-up

 * - displays backend increase/repetition warnings

 * - allows draft null values

 * - validates configured minimum/maximum values

 * - autosaves after a short delay

 * - serializes ROM saves so each uses a fresh entityVersion

 * - refreshes ClinicalNoteMain.entityVersion after each save

 */

export default function ClinicalNoteRomSection({

  clinicalNoteId,

  disabled = false,

  onSaved,

  onVersionChange,

  navigationCoordinator,

}) {

  const [

    rows,

    setRows,

  ] = useState([]);



  const [

    loading,

    setLoading,

  ] = useState(true);



  const [

    loadError,

    setLoadError,

  ] = useState('');



  const [

    rowStatuses,

    setRowStatuses,

  ] = useState({});



  const [

    rowSaveErrors,

    setRowSaveErrors,

  ] = useState({});



  const [

    editTick,

    setEditTick,

  ] = useState(0);



  const [

    initialized,

    setInitialized,

  ] = useState(false);



  /*

   * Autosave coordination refs.

   */

  const rowsRef =

    useRef([]);



  const versionRef =

    useRef(null);



  const rowRevisionRef =

    useRef({});



  const dirtyKeysRef =

    useRef(new Set());



  const failedKeysRef =

    useRef(new Set());



  const savingRef =

    useRef(false);



  const mountedRef =

    useRef(true);



  const flushSaveRef =

    useRef(null);

  // Track all changed rows, including invalid and failed rows removed
  // from the retry queue. These must block navigation until saved.
  const unsavedKeysRef = useRef(new Set());
  const inFlightSaveRef = useRef(null);
  const initializedRef = useRef(false);
  const loadErrorRef = useRef('');




  useEffect(() => {

    rowsRef.current =

      rows;

  }, [rows]);



  useEffect(() => {

    return () => {

      mountedRef.current =

        false;

    };

  }, []);



  /**

   * Load configured ROM rows and the current note version.

   */

  useEffect(() => {

    let cancelled = false;



    async function loadRomSection() {

      setLoading(true);

      loadErrorRef.current = '';
      initializedRef.current = false;
      setLoadError('');

      setInitialized(false);

      setRowStatuses({});

      setRowSaveErrors({});



      unsavedKeysRef.current = new Set();
      dirtyKeysRef.current =

        new Set();



      failedKeysRef.current =

        new Set();



      rowRevisionRef.current =

        {};



      savingRef.current =

        false;



      try {

        const [

          romResponse,

          noteResponse,

        ] =

          await Promise.all([

            clinicalNoteService

              .getRom(

                clinicalNoteId

              ),



            clinicalNoteService

              .getClinicalNote(

                clinicalNoteId

              ),

          ]);



        if (cancelled) {

          return;

        }



        const version =

          resolveEntityVersion(

            noteResponse

          );



        if (

          version === null ||

          version === undefined

        ) {

          throw new Error(

            'The clinical note version could not be loaded.'

          );

        }



        versionRef.current =

          version;



        const normalizedRows =

          sortRomRows(

            (romResponse || []).map(

              normalizeRomRow

            )

          );



        const initialStatuses =

          {};



        const initialRevisions =

          {};



        normalizedRows.forEach(

          (row) => {

            const key =

              getRomKey(row);



            initialStatuses[key] =

              'saved';



            initialRevisions[key] =

              0;

          }

        );



        rowsRef.current =

          normalizedRows;



        rowRevisionRef.current =

          initialRevisions;



        setRows(

          normalizedRows

        );



        setRowStatuses(

          initialStatuses

        );



        initializedRef.current = true;
        setInitialized(true);

      } catch (error) {

        if (cancelled) {

          return;

        }



        loadErrorRef.current = error?.message || 'ROM measurements could not be loaded.';
        setLoadError(loadErrorRef.current);

      } finally {

        if (!cancelled) {

          setLoading(false);

        }

      }

    }



    if (clinicalNoteId) {

      loadRomSection();

    } else {

      setLoadError(

        'Clinical note ID is required.'

      );



      setLoading(false);

    }



    return () => {

      cancelled = true;

    };

  }, [clinicalNoteId]);



  /**

   * Refresh the parent clinical-note optimistic-locking version.

   */

  const refreshEntityVersion =

    useCallback(

      async () => {

        const noteResponse =

          await clinicalNoteService

            .getClinicalNote(

              clinicalNoteId

            );



        const newVersion =

          resolveEntityVersion(

            noteResponse

          );



        if (

          newVersion === null ||

          newVersion ===

            undefined

        ) {

          throw new Error(

            'ROM was saved, but the updated clinical note version could not be loaded.'

          );

        }



        versionRef.current =

          newVersion;



        if (

          typeof onVersionChange ===

          'function'

        ) {

          onVersionChange(

            newVersion,

            noteResponse

          );

        }



        return noteResponse;

      },

      [

        clinicalNoteId,

        onVersionChange,

      ]

    );



  /**

   * Mark one ROM row as edited.

   */

  function updateRowField(

    key,

    field,

    value

  ) {

    if (disabled) {

      return;

    }



    const nextRows =

      rowsRef.current.map(

        (row) =>

          getRomKey(row) === key

            ? {

                ...row,

                [field]: value,

              }

            : row

      );



    rowsRef.current =

      nextRows;



    setRows(

      nextRows

    );



    rowRevisionRef.current = {

      ...rowRevisionRef.current,



      [key]:

        (

          rowRevisionRef

            .current[key] ??

          0

        ) + 1,

    };



    /*

     * A new clinician edit is allowed to retry a row that previously

     * had a network/version error.

     */

    failedKeysRef.current.delete(

      key

    );



    unsavedKeysRef.current.add(key);
    dirtyKeysRef.current.add(

      key

    );



    setRowSaveErrors(

      (current) => ({

        ...current,

        [key]: '',

      })

    );



    const editedRow =

      nextRows.find(

        (row) =>

          getRomKey(row) === key

      );



    const errors =

      editedRow

        ? validateRomRow(

            editedRow

          )

        : {};



    setRowStatuses(

      (current) => ({

        ...current,



        [key]:

          Object.keys(errors)

            .length > 0

            ? 'invalid'

            : 'unsaved',

      })

    );



    setEditTick(

      (current) =>

        current + 1

    );

  }



  /**

   * Update a row after a successful backend save.

   */

  function applySuccessfulSave(

    key,

    response,

    revisionBeingSaved

  ) {

    const currentRevision =

      rowRevisionRef.current[

        key

      ] ?? 0;



    const preserveDraft =

      currentRevision !==

      revisionBeingSaved;



    const nextRows =

      rowsRef.current.map(

        (row) => {

          if (

            getRomKey(row) !==

            key

          ) {

            return row;

          }



          return mergeSavedRomRow(

            row,

            response,

            preserveDraft

          );

        }

      );



    rowsRef.current =

      sortRomRows(

        nextRows

      );



    setRows(

      rowsRef.current

    );



    if (!preserveDraft) unsavedKeysRef.current.delete(key);

    if (preserveDraft) {

      dirtyKeysRef.current.add(

        key

      );



      setRowStatuses(

        (current) => ({

          ...current,

          [key]: 'unsaved',

        })

      );

    } else {

      setRowStatuses(

        (current) => ({

          ...current,

          [key]: 'saved',

        })

      );

    }

  }



  /**

   * Save dirty ROM rows one at a time.

   *

   * This is important because every save modifies the parent

   * ClinicalNoteMain version. Sending several PUT requests in parallel

   * would cause multiple requests to reuse the same expectedVersion.

   */

  const flushSaveQueue =

    useCallback(

      async () => {

        if (

          disabled ||

          !initialized ||

          savingRef.current

        ) {

          return;

        }



        savingRef.current =

          true;



        try {

          while (

            mountedRef.current &&

            dirtyKeysRef.current

              .size > 0

          ) {

            const key =

              Array.from(

                dirtyKeysRef.current

              ).find(

                (candidate) =>

                  !failedKeysRef

                    .current

                    .has(candidate)

              );



            if (!key) {

              break;

            }



            /*

             * Remove this key while its save is in flight.

             *

             * If the clinician edits it during the request,

             * updateRowField() adds it back to the dirty set.

             */

            dirtyKeysRef.current.delete(

              key

            );



            const row =

              rowsRef.current.find(

                (candidate) =>

                  getRomKey(

                    candidate

                  ) === key

              );



            if (!row) {

              continue;

            }



            const validationErrors =

              validateRomRow(

                row

              );



            if (

              Object.keys(

                validationErrors

              ).length > 0

            ) {

              setRowStatuses(

                (current) => ({

                  ...current,

                  [key]:

                    'invalid',

                })

              );



              continue;

            }



            const revisionBeingSaved =

              rowRevisionRef.current[

                key

              ] ?? 0;



            setRowStatuses(

              (current) => ({

                ...current,

                [key]:

                  'saving',

              })

            );



            setRowSaveErrors(

              (current) => ({

                ...current,

                [key]: '',

              })

            );



            try {

              if (

                versionRef.current ===

                  null ||

                versionRef.current ===

                  undefined

              ) {

                await refreshEntityVersion();

              }



              const response =

                await clinicalNoteService

                  .saveRom(

                    clinicalNoteId,

                    {

                      expectedVersion:

                        versionRef.current,



                      romTemplateId:

                        row.romTemplateId,



                      measurementSide:

                        row.measurementSide,



                      measurementValue:

                        toNullableInteger(

                          row.draftMeasurementValue

                        ),



                      comments:

                        row.draftComments

                          ?.trim() ||

                        null,

                    }

                  );



              if (

                !mountedRef.current

              ) {

                return;

              }



              /*

               * Every section autosave updates ClinicalNoteMain.

               * Refresh the version before another ROM row is saved.

               */

              const noteResponse =

                await refreshEntityVersion();



              if (

                !mountedRef.current

              ) {

                return;

              }



              applySuccessfulSave(

                key,

                response,

                revisionBeingSaved

              );



              if (

                typeof onSaved ===

                'function'

              ) {

                onSaved(

                  response,

                  noteResponse

                );

              }

            } catch (error) {

              if (

                !mountedRef.current

              ) {

                return;

              }



              /*

               * Do not automatically retry a failed request forever.

               *

               * The row remains visibly failed and can be retried

               * manually or after the clinician edits it again.

               */

              failedKeysRef.current.add(

                key

              );



              dirtyKeysRef.current.delete(

                key

              );



              setRowStatuses(

                (current) => ({

                  ...current,

                  [key]: 'error',

                })

              );



              setRowSaveErrors(

                (current) => ({

                  ...current,



                  [key]:

                    error?.message ||

                    'ROM measurement could not be autosaved.',

                })

              );

            }

          }

        } finally {

          savingRef.current =

            false;



          const hasRetryableDirtyRows =

            Array.from(

              dirtyKeysRef.current

            ).some(

              (key) =>

                !failedKeysRef

                  .current

                  .has(key)

            );



          if (

            mountedRef.current &&

            hasRetryableDirtyRows

          ) {

            window.setTimeout(

              () => {

                flushSaveRef

                  .current?.();

              },

              150

            );

          }

        }

      },

      [

        clinicalNoteId,

        disabled,

        initialized,

        onSaved,

        refreshEntityVersion,

      ]

    );



  /** Share one awaitable save promise between autosave and navigation. */
  useEffect(() => {
    flushSaveRef.current = () => {
      if (inFlightSaveRef.current) return inFlightSaveRef.current;
      const running = flushSaveQueue();
      const tracked = Promise.resolve(running).finally(() => {
        if (inFlightSaveRef.current === tracked) {
          inFlightSaveRef.current = null;
        }
      });
      inFlightSaveRef.current = tracked;
      return tracked;
    };
  }, [flushSaveQueue]);

  /**
   * Navigation must wait for an active request and flush edits made
   * while that request was running. Invalid/failed drafts are not lost.
   */
  useEffect(() => {
    if (!navigationCoordinator || !clinicalNoteId) return undefined;
    return navigationCoordinator.register(clinicalNoteId, 'rom', {
      flush: async () => {
        if (inFlightSaveRef.current) await inFlightSaveRef.current;
        if (dirtyKeysRef.current.size > 0) {
          await flushSaveRef.current?.();
        }
        if (inFlightSaveRef.current) await inFlightSaveRef.current;
      },
      getState: () => {
        const unsaved = [...unsavedKeysRef.current];
        const invalid = unsaved.some((key) => {
          const row = rowsRef.current.find(item => getRomKey(item) === key);
          return row && Object.keys(validateRomRow(row)).length > 0;
        });
        return {
          dirty: unsaved.length > 0 || !initializedRef.current,
          saving: Boolean(inFlightSaveRef.current),
          invalid,
          error: loadErrorRef.current || (!initializedRef.current ? 'ROM is still loading.' : '') || (
            unsaved.some(key => failedKeysRef.current.has(key))
              ? 'A ROM measurement failed to save. Correct it or use Retry Save before switching visits.'
              : ''
          ),
        };
      },
    });
  }, [navigationCoordinator, clinicalNoteId]);



  /**

   * Debounced autosave trigger.

   */

  useEffect(() => {

    if (

      !initialized ||

      disabled ||

      dirtyKeysRef.current

        .size === 0

    ) {

      return undefined;

    }



    const timer =

      window.setTimeout(

        () => {

          flushSaveRef

            .current?.();

        },

        AUTOSAVE_DELAY_MS

      );



    return () => {

      window.clearTimeout(

        timer

      );

    };

  }, [

    editTick,

    initialized,

    disabled,

  ]);



  /**

   * Manual retry after an autosave/network/version failure.

   */

  function retryRow(key) {

    if (disabled) {

      return;

    }



    failedKeysRef.current.delete(

      key

    );



    dirtyKeysRef.current.add(

      key

    );



    setRowSaveErrors(

      (current) => ({

        ...current,

        [key]: '',

      })

    );



    setRowStatuses(

      (current) => ({

        ...current,

        [key]: 'unsaved',

      })

    );



    setEditTick(

      (current) =>

        current + 1

    );

  }



  /**

   * Validation state for all currently displayed rows.

   */

  const validationByKey =

    useMemo(() => {

      const result = {};



      rows.forEach(

        (row) => {

          result[

            getRomKey(row)

          ] =

            validateRomRow(

              row

            );

        }

      );



      return result;

    }, [rows]);



  /**

   * Overall section autosave state.

   */

  const overallStatus =

    useMemo(() => {

      const values =

        Object.values(

          rowStatuses

        );



      if (

        values.includes(

          'saving'

        )

      ) {

        return 'Saving…';

      }



      if (

        values.includes(

          'error'

        )

      ) {

        return 'Autosave error';

      }



      if (

        values.includes(

          'invalid'

        )

      ) {

        return 'Needs correction';

      }



      if (

        values.includes(

          'unsaved'

        )

      ) {

        return 'Unsaved changes';

      }



      return disabled

        ? 'Read only'

        : 'Saved';

    }, [

      rowStatuses,

      disabled,

    ]);



  /**

   * Number of unresolved backend ROM warnings.

   */

  const unresolvedWarningCount =

    useMemo(

      () =>

        rows.filter(

          (row) =>

            (

              row.increaseWarning ||

              row.repetitionWarning

            ) &&

            !row.warningAcknowledged

        ).length,

      [rows]

    );



  if (loading) {

    return (

      <section

        style={{

          border:

            '1px solid #ddd',

          borderRadius: 10,

          padding: 16,

          background: '#fff',

        }}

      >

        Loading ROM…

      </section>

    );

  }



  if (loadError) {

    return (

      <section

        style={{

          border:

            '1px solid #c62828',

          borderRadius: 10,

          padding: 16,

          background:

            '#fff5f5',

        }}

      >

        <strong>

          ROM section could not be

          loaded

        </strong>



        <p

          style={{

            marginBottom: 0,

          }}

        >

          {loadError}

        </p>

      </section>

    );

  }



  return (

    <section

      style={{

        border:

          '1px solid #d7d7d7',

        borderRadius: 10,

        background: '#fff',

        overflow: 'hidden',

      }}

    >

      {/* =====================================================

          SECTION HEADER

          ===================================================== */}

      <div

        style={{

          display: 'flex',

          justifyContent:

            'space-between',

          alignItems: 'center',

          gap: 12,

          flexWrap: 'wrap',

          padding:

            '13px 16px',

          background:

            '#f7f8fa',

          borderBottom:

            '1px solid #ddd',

        }}

      >

        <div>

          <h3

            style={{

              margin: 0,

              fontSize: 17,

            }}

          >

            Range of Motion

          </h3>



          <div

            style={{

              marginTop: 3,

              fontSize: 12,

              color: '#666',

            }}

          >

            ROM measurements are

            saved individually and

            compared with the most

            recent prior treatment

            date.

          </div>

        </div>



        <div

          style={{

            textAlign: 'right',

          }}

        >

          <div

            aria-live="polite"

            style={{

              fontSize: 13,

              fontWeight: 700,

            }}

          >

            {overallStatus}

          </div>



          {unresolvedWarningCount >

            0 && (

            <div

              style={{

                marginTop: 3,

                fontSize: 12,

                color: '#9a6700',

                fontWeight: 700,

              }}

            >

              {unresolvedWarningCount}{' '}

              unresolved ROM{' '}

              {unresolvedWarningCount ===

              1

                ? 'warning'

                : 'warnings'}

            </div>

          )}

        </div>

      </div>



      <div

        style={{

          padding: 16,

        }}

      >

        {/* ===================================================

            INFORMATION

            =================================================== */}

        <p className="clinical-rom-help">
          Enter ROM values below. Open <strong>Previous / Change</strong> to see the
          comparison. Optional comments stay minimized. Existing clinical
          warnings remain visible and are handled through the warning workflow.
        </p>



        {/* ===================================================

            NO CONFIGURATION

            =================================================== */}

        {rows.length === 0 && (

          <div

            style={{

              border:

                '1px dashed #bbb',

              borderRadius: 8,

              padding: 16,

              color: '#666',

              background:

                '#fafafa',

            }}

          >

            No ROM measurements are

            configured for this

            clinical note's body

            part.

          </div>

        )}



        {/* ===================================================

            ROM ROWS

            =================================================== */}

        {rows.length > 0 && (

          <div

            style={{

              display: 'grid',

              gap: 12,

            }}

          >

            {rows.map(

              (row) => {

                const key =

                  getRomKey(

                    row

                  );



                return (

                  <RomMeasurementRow

                    key={key}

                    row={row}

                    disabled={

                      disabled

                    }

                    status={

                      rowStatuses[

                        key

                      ] ||

                      'saved'

                    }

                    saveError={

                      rowSaveErrors[

                        key

                      ] ||

                      ''

                    }

                    validationError={

                      validationByKey[

                        key

                      ]

                        ?.measurementValue ||

                      ''

                    }

                    onMeasurementChange={(

                      value

                    ) =>

                      updateRowField(

                        key,

                        'draftMeasurementValue',

                        value

                      )

                    }

                    onCommentsChange={(

                      value

                    ) =>

                      updateRowField(

                        key,

                        'draftComments',

                        value

                      )

                    }

                    onRetry={() =>

                      retryRow(

                        key

                      )

                    }

                  />

                );

              }

            )}

          </div>

        )}



        {/* ===================================================

            READ ONLY

            =================================================== */}

        {disabled && (

          <div

            style={{

              marginTop: 14,

              fontSize: 13,

              color: '#666',

            }}

          >

            ROM is read only because

            this clinical note is no

            longer in the normal

            Draft editing state.

          </div>

        )}

      </div>

    </section>

  );

}